import { useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Card, Modal, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleFilled, EyeOutlined, InboxOutlined, LinkOutlined, MessageOutlined, SyncOutlined, UserAddOutlined } from '@ant-design/icons';
import { ENROLLMENT_STATUSES, type EnrollmentItem, type SequenceItem } from '@crm/shared';
import { useEnroll, useEnrollmentAction, useEnrollments } from '../../features/sequences/api';
import { NameCell, TableSearch } from '../../components/NameCell';
import { ContactSelect } from '../../components/ContactSelect';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { contactName } from '../../features/contacts/api';
import { errorMessage } from '../../api/client';
import { fmtDateTime, plural } from '../../utils/format';

export const ENROLLMENT_COLOR: Record<string, string> = { ACTIVE: 'processing', PAUSED: 'warning', COMPLETED: 'default', REPLIED: 'success', BOUNCED: 'error', UNSUBSCRIBED: 'default', FAILED: 'error', REMOVED: 'default' };
const label = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function PeopleTab({ sequence }: { sequence: SequenceItem }) {
  const { message, modal } = App.useApp();
  const [status, setStatus] = useState<string>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([]);
  const rows = useEnrollments(sequence.id, { status, q: q || undefined, page, pageSize });
  const enroll = useEnroll();
  const act = useEnrollmentAction();
  const steps = sequence.steps.length;

  const bulk = (action: 'pause' | 'resume' | 'remove' | 'restart') => {
    const run = () =>
      act
        .mutateAsync({ id: sequence.id, ids: selected, action })
        .then((r) => {
          message.success(`${plural(r.updated, 'person', 'people')} updated`);
          setSelected([]);
        })
        .catch((e) => message.error(errorMessage(e)));
    if (action === 'restart') modal.confirm({ title: `Restart ${plural(selected.length, 'person', 'people')} from step 1?`, content: 'They get the whole sequence again, in a new thread.', okText: 'Restart', onOk: run });
    else if (action === 'remove') modal.confirm({ title: `Remove ${plural(selected.length, 'person', 'people')} from the sequence?`, content: 'They get no more emails from it.', okText: 'Remove', okButtonProps: { danger: true }, onOk: run });
    else void run();
  };

  const enrollAudience = () =>
    enroll
      .mutateAsync({ id: sequence.id })
      .then((r) => message.success(r.added ? `${plural(r.added, 'person', 'people')} added` : 'Everyone in the audience is already in'))
      .catch((e) => message.error(errorMessage(e)));

  const addPicked = () =>
    enroll
      .mutateAsync({ id: sequence.id, contactIds: picked.map((p) => p.id) })
      .then((r) => {
        message.success(`${plural(r.added, 'person', 'people')} added${r.skipped ? `, ${r.skipped} skipped (already in, in another sequence, or can't be emailed)` : ''}`);
        setPicked([]);
        setAdding(false);
      })
      .catch((e) => message.error(errorMessage(e)));

  const where = (e: EnrollmentItem) => {
    if (e.status === 'ACTIVE' || e.status === 'PAUSED') {
      const next = sequence.steps[e.stepIndex];
      return (
        <Typography.Text style={{ fontSize: 12 }}>
          Step {Math.min(e.stepIndex + 1, steps)} of {steps}
          {next && e.status === 'ACTIVE' && e.nextRunAt && <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{new Date(e.nextRunAt) <= new Date() ? 'Due, waiting for the send window' : `Next ${fmtDateTime(e.nextRunAt)}`}</Typography.Text>}
        </Typography.Text>
      );
    }
    return <Typography.Text type="secondary" style={{ fontSize: 12 }}>{e.finishReason ?? '—'}{e.finishedAt && <span style={{ display: 'block' }}>{fmtDateTime(e.finishedAt)}</span>}</Typography.Text>;
  };

  return (
    <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={q} onSearch={(v) => { setQ(v); setPage(1); }} placeholder="Search people" />
          <Select
            aria-label="Status"
            value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            style={{ width: 150 }}
            options={[{ value: 'all', label: 'All statuses' }, ...ENROLLMENT_STATUSES.map((s) => ({ value: s, label: label(s) }))]}
          />
        </div>
        <div className="toolbar-group">
          {selected.length > 0 ? (
            <Space wrap>
              <Typography.Text type="secondary">{selected.length} selected</Typography.Text>
              <Button size="small" onClick={() => bulk('pause')}>Pause</Button>
              <Button size="small" onClick={() => bulk('resume')}>Resume</Button>
              <Button size="small" onClick={() => bulk('restart')}>Restart</Button>
              <Button size="small" danger onClick={() => bulk('remove')}>Remove</Button>
            </Space>
          ) : (
            <Space wrap>
              <Tooltip title="Add everyone in the audience who isn't in yet">
                <Button icon={<SyncOutlined />} loading={enroll.isPending && !adding} onClick={enrollAudience}>Add audience</Button>
              </Tooltip>
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => setAdding(true)}>Add people</Button>
            </Space>
          )}
        </div>
      </div>
      <Table<EnrollmentItem>
        rowKey="id"
        className="table-tight"
        loading={tableLoading(rows)}
        locale={tableLocale(rows, <EmptyState description={q || status !== 'all' ? 'Nobody matches this search or filter.' : 'Nobody in this sequence yet. Activating it adds everyone in the audience; you can also add people by hand.'} />)}
        dataSource={rows.data?.items ?? []}
        rowSelection={{ selectedRowKeys: selected, onChange: (k) => setSelected(k as string[]) }}
        scroll={{ x: 960 }}
        tableLayout="fixed"
        pagination={{ current: page, pageSize, total: rows.data?.total ?? 0, showSizeChanger: true, onChange: (p, s) => { setPage(p); setPageSize(s); }, showTotal: (t) => plural(t, 'person', 'people') }}
        columns={[
          {
            title: 'Person',
            key: 'contact',
            width: 230,
            render: (_, e) => <NameCell name={contactName(e.contact)} sub={e.contact.email ?? undefined} to={`/contacts/${e.contact.id}`} size={30} />,
          },
          { title: 'Organization', key: 'org', width: 150, ellipsis: true, render: (_, e) => (e.contact.organization ? <Link to={`/organizations/${e.contact.organization.id}`}>{e.contact.organization.name}</Link> : '—') },
          { title: 'Status', dataIndex: 'status', width: 110, render: (s: string, e) => <Tooltip title={e.error ?? undefined}><Tag color={ENROLLMENT_COLOR[s]}>{label(s)}</Tag></Tooltip> },
          { title: 'Where', key: 'where', width: 190, render: (_, e) => where(e) },
          {
            title: 'Engagement',
            key: 'eng',
            width: 130,
            render: (_, e) => (
              <Space size={8}>
                <Tooltip title={e.openedAt ? `Opened ${fmtDateTime(e.openedAt)}` : 'Not opened'}><EyeOutlined style={{ color: e.openedAt ? '#1677ff' : '#d9d9d9' }} /></Tooltip>
                <Tooltip title={e.clickedAt ? `Clicked ${fmtDateTime(e.clickedAt)}` : 'No clicks'}><LinkOutlined style={{ color: e.clickedAt ? '#1677ff' : '#d9d9d9' }} /></Tooltip>
                <Tooltip title={e.repliedAt ? `Replied ${fmtDateTime(e.repliedAt)}` : 'No reply'}><MessageOutlined style={{ color: e.repliedAt ? '#52c41a' : '#d9d9d9' }} /></Tooltip>
                {e.repliedAt && <CheckCircleFilled style={{ color: '#52c41a' }} />}
              </Space>
            ),
          },
          { title: 'Agent', key: 'agent', width: 120, ellipsis: true, render: (_, e) => e.agent?.name ?? '—' },
          { title: 'Last email', dataIndex: 'lastSentAt', width: 130, render: (v: string | null) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v ? fmtDateTime(v) : '—'}</Typography.Text> },
          {
            key: 'thread',
            width: 50,
            align: 'right',
            render: (_, e) =>
              e.threadId ? (
                <Tooltip title="Open the conversation in the Inbox">
                  <Link to={`/inbox?thread=${e.threadId}&status=all`} aria-label="Open conversation"><InboxOutlined /></Link>
                </Tooltip>
              ) : null,
          },
        ]}
      />
      <Modal title="Add people" open={adding} onCancel={() => setAdding(false)} okText={picked.length ? `Add ${plural(picked.length, 'person', 'people')}` : 'Add'} okButtonProps={{ disabled: !picked.length }} confirmLoading={enroll.isPending} onOk={addPicked} destroyOnHidden>
        <Typography.Paragraph type="secondary">Pick people one by one. Anyone who can't be emailed (unsubscribed, bounced, no address) or is already in is skipped.</Typography.Paragraph>
        <ContactSelect
          value={null}
          onChange={(id, c) => {
            if (id && !picked.some((p) => p.id === id)) setPicked([...picked, { id, name: c ? contactName(c) : id }]);
          }}
        />
        <div style={{ marginTop: 12 }}>
          {picked.map((p) => (
            <Tag key={p.id} closable onClose={() => setPicked(picked.filter((x) => x.id !== p.id))} style={{ marginBottom: 6 }}>{p.name}</Tag>
          ))}
        </div>
      </Modal>
    </Card>
  );
}
